import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface VerificationStepProps {
  userName: string;
  userEmail: string;
  onVerified: () => void;
  onBack: () => void;
}

export default function VerificationStep({ userName, userEmail, onVerified, onBack }: VerificationStepProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleChange = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = cleaned;
    setCode(newCode);
    setError('');
    if (cleaned && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length < 6) {
      setError('Por favor introduce los 6 dígitos de tu Clave de Frecuencia.');
      return;
    }
    setLoading(true);
    setError('');

    const { error: err } = await supabase.auth.verifyOtp({
      email: userEmail,
      token: fullCode,
      type: 'email',
    });

    if (err) {
      setError('Clave incorrecta o expirada. Verifica tu correo e inténtalo de nuevo.');
    } else {
      onVerified();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(60);
    setCode(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus();
    await supabase.auth.signInWithOtp({
      email: userEmail,
      options: { shouldCreateUser: true },
    });
  };

  const isComplete = code.every((d) => d !== '');

  return (
    <div className="space-y-7">
      <div className="text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-5" style={{ border: '1px solid rgba(139,157,131,0.4)', background: 'rgba(139,157,131,0.08)' }}>
          <i className="ri-mail-send-line text-[#8B9D83] text-2xl" />
        </div>
        <h3 className="text-[#F8F6F3] font-semibold text-xl mb-2">Clave de Alineación enviada</h3>
        {userName && <p className="text-[#8B9D83] text-sm mb-1">Bienvenido/a, {userName}</p>}
        <p className="text-[#F8F6F3]/50 text-sm leading-relaxed max-w-sm mx-auto">
          Hemos enviado tu <span className="text-[#8B9D83]">Código de Activación</span> a{' '}
          <span className="text-[#F8F6F3]/80">{userEmail}</span>. Revisa tu bandeja.
        </p>
      </div>

      <div>
        <p className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[3px] text-center mb-4">
          — Introduce tu Clave de Frecuencia —
        </p>
        <div className="flex items-center justify-center gap-2" onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-12 h-14 text-center text-xl font-bold rounded-lg transition-all duration-200 focus:outline-none"
              style={{
                background: digit ? 'rgba(139,157,131,0.12)' : 'rgba(248,246,243,0.04)',
                border: digit ? '1px solid rgba(139,157,131,0.7)' : '1px solid rgba(248,246,243,0.12)',
                color: digit ? '#8B9D83' : 'rgba(248,246,243,0.4)',
                boxShadow: digit ? '0 0 12px rgba(139,157,131,0.2)' : 'none',
              }}
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-[#C97B63]/10 border border-[#C97B63]/30 rounded-lg px-4 py-2.5 text-center">
          <p className="text-[#C97B63] text-xs">{error}</p>
        </div>
      )}

      <button
        type="button"
        onClick={handleVerify}
        disabled={loading || !isComplete}
        className="w-full font-bold text-sm py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
        style={{
          background: isComplete ? '#8B9D83' : 'rgba(139,157,131,0.2)',
          color: isComplete ? '#1A1C20' : 'rgba(248,246,243,0.3)',
          border: '1px solid rgba(139,157,131,0.3)',
        }}
      >
        {loading ? <><i className="ri-loader-4-line animate-spin" /> Validando Frecuencia...</> : <><i className="ri-key-2-line" /> Validar Frecuencia de Nodo</>}
      </button>

      <div className="text-center">
        <p className="text-[#F8F6F3]/30 text-xs mb-1">¿No recibiste el código?</p>
        {resendCooldown > 0 ? (
          <p className="text-[#F8F6F3]/25 text-xs">Reenviar en <span className="text-[#C97B63]">{resendCooldown}s</span></p>
        ) : (
          <button type="button" onClick={handleResend} className="text-[#C97B63] text-xs hover:underline cursor-pointer">
            Reenviar Clave de Alineación
          </button>
        )}
      </div>

      <button type="button" onClick={onBack} className="w-full text-[#F8F6F3]/30 text-xs hover:text-[#F8F6F3]/60 cursor-pointer flex items-center justify-center gap-1 pt-1">
        <i className="ri-arrow-left-line" /> Volver al formulario
      </button>
    </div>
  );
}
